import { Icon, IconButton, IconType } from '@assembly-js/design-system';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@material-ui/core';
import clsx from 'clsx';
import { useMemo, useRef, useState } from 'react';

import { useMessagingTheme } from 'src/features/messages/messagingThemeContext';
import { addCustomCopyBehavior } from 'src/utils/ClipboardUtils';

type DeleteClickHandler = () => void;
type EditClickHandler = (ev: React.MouseEvent<HTMLElement>) => void;

interface Action {
  label: string;
  icon: IconType;
  onClick: DeleteClickHandler | EditClickHandler;
  isDestructive?: boolean;
}

interface MessageActionsMenuProps {
  onDeleteClicked: DeleteClickHandler;
  onEditClicked: EditClickHandler;
}

export const MessageActionsMenu = ({
  onDeleteClicked,
  onEditClicked,
}: MessageActionsMenuProps) => {
  const { isSidebarTheme } = useMessagingTheme();
  const [isOpen, setIsOpen] = useState(false);
  const anchorElRef = useRef<HTMLDivElement>(null);

  const actions = useMemo<Action[]>(
    () => [
      { label: 'Edit message', icon: 'Edit', onClick: onEditClicked },
      {
        label: 'Delete message',
        icon: 'Trash',
        onClick: onDeleteClicked,
        isDestructive: true,
      },
    ],
    [onEditClicked, onDeleteClicked],
  );

  return (
    <div
      className={clsx(
        'sm:group-hover/messageWrapper:flex items-center justify-center p-1 rounded-md border border-solid border-non-hover-border absolute -top-3 shadow-[0_2px_4px_-2px_rgba(0,0,0,0.06)] z-10',
        isSidebarTheme ? 'bg-gray-100 right-0' : 'bg-white right-28',
        isOpen ? 'flex' : 'hidden',
      )}
      ref={anchorElRef}
      {...addCustomCopyBehavior({ excludeFromCopy: true })}
    >
      <IconButton
        className={clsx(
          'p-[5px] border-none [&_svg]:w-3.5 [&_svg]:h-3.5',
          isSidebarTheme && 'hover:bg-secondary-hover',
        )}
        icon="MoreVertical"
        variant="minimal"
        onClick={() => setIsOpen(true)}
      />
      <Menu
        open={isOpen}
        anchorEl={anchorElRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: -2,
          horizontal: 'right',
        }}
        getContentAnchorEl={null}
        onClose={() => setIsOpen(false)}
        classes={{
          paper:
            'bg-white border border-solid border-secondary-hover shadow-popover-050',
        }}
      >
        {actions.map(({ icon, label, onClick, isDestructive }, idx) => (
          <MenuItem
            key={idx}
            classes={{ root: 'px-3 py-0.5' }}
            onClick={(e) => {
              onClick(e);
              setIsOpen(false);
            }}
          >
            <ListItemIcon
              classes={{
                root: clsx(
                  'min-w-max mr-3',
                  isDestructive ? 'text-error-primary' : 'text-black-heading',
                ),
              }}
            >
              <Icon className="w-3.5 h-3.5" icon={icon} />
            </ListItemIcon>
            <ListItemText
              classes={{
                primary: clsx(
                  'text-base',
                  isDestructive ? 'text-error-primary' : 'text-black-heading',
                ),
              }}
            >
              {label}
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};
